import { createContext, useContext } from 'react'

// Selected dashboard month as "YYYY-MM" (feedback #2). All dashboard sections
// read this and refetch when it changes.
export interface DashboardPeriodValue {
  period: string
  // Month bounds derived from period, for endpoints taking date_from / date_to.
  dateFrom: string
  dateTo: string
  // True when the selected month is the current calendar month.
  isCurrentMonth: boolean
}

export const DashboardPeriodContext = createContext<DashboardPeriodValue | null>(
  null
)

export function useDashboardPeriod(): DashboardPeriodValue {
  const ctx = useContext(DashboardPeriodContext)
  if (!ctx) {
    throw new Error(
      'useDashboardPeriod must be used within a DashboardPeriodContext provider'
    )
  }
  return ctx
}

// Current month as "YYYY-MM".
export function currentPeriod(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Derive the value object (bounds + flags) from a "YYYY-MM" period string.
export function buildPeriodValue(period: string): DashboardPeriodValue {
  const [yearStr, monthStr] = period.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr) // 1-12
  // First and last day of the selected month (local), formatted as YYYY-MM-DD.
  const first = new Date(year, month - 1, 1)
  const last = new Date(year, month, 0)
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`
  const now = new Date()
  const isCurrentMonth =
    now.getFullYear() === year && now.getMonth() + 1 === month
  return {
    period,
    dateFrom: fmt(first),
    dateTo: fmt(last),
    isCurrentMonth,
  }
}
